import { COUNTRY_CODES, countryName, toCountryCode } from '@clockwise/shared';
import type { FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';
import { Combobox } from '@/components/custom/combobox';
import { Label } from '@/components/ui/label';

// One option per ISO-3166 alpha-2 code, labeled with the English name.
// Module-level: the list is static and shared by every mounted select.
const OPTIONS = COUNTRY_CODES.map((code) => ({ code, name: countryName(code) })).sort((a, b) =>
  a.name.localeCompare(b.name),
);

type CountryOption = (typeof OPTIONS)[number];

function matchesQuery(option: CountryOption, query: string): boolean {
  const q = query.toLowerCase();
  return option.name.toLowerCase().includes(q) || option.code.toLowerCase().startsWith(q);
}

export interface CountrySelectProps {
  id?: string;
  /** Stored country value — an alpha-2 code, or legacy free text from rows
   *  that predate ISO storage (resolved to a code for display when
   *  possible). */
  value: string | null | undefined;
  /** Emits the alpha-2 code, or null when cleared. */
  onChange: (code: string | null) => void;
  disabled?: boolean;
  hasError?: boolean;
  placeholder?: string;
}

/**
 * Country picker — stores ISO-3166 alpha-2, displays English names.
 *
 * Legacy values: a stored name that resolves ("United States" → US) just
 * displays as if the code were stored; submitting unchanged is fine because
 * the API normalizes names too. An unresolvable value renders the select
 * empty plus an inline warning, and the form's validation blocks submit
 * until the user reselects — never silently cleared, never silently kept.
 */
export function CountrySelect({
  id,
  value,
  onChange,
  disabled,
  hasError,
  placeholder = 'Select country…',
}: CountrySelectProps) {
  const resolved = toCountryCode(value) ?? null;
  const unrecognized = value && !resolved ? value : null;

  return (
    <div className="w-full">
      <Combobox<CountryOption>
        items={OPTIONS}
        value={resolved}
        onChange={(option) => onChange(option ? option.code : null)}
        getId={(o) => o.code}
        getLabel={(o) => o.name}
        filterFn={matchesQuery}
        renderTrailing={(o) => o.code}
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        hasError={hasError || Boolean(unrecognized)}
      />
      {unrecognized ? (
        <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
          Saved value “{unrecognized}” isn’t a recognized country — select one to replace it.
        </p>
      ) : null}
    </div>
  );
}

/** Error copy shared by every form schema's country refine. */
export const COUNTRY_FIELD_ERROR = 'Select a country from the list';

export interface CountrySelectFieldProps<TValues extends FieldValues> {
  form: UseFormReturn<TValues>;
  /** Form path of a string country field ('' = not provided). */
  name: Path<TValues>;
  id: string;
  label?: string;
  disabled?: boolean;
}

/**
 * Form-bound country field: label + CountrySelect + inline error, wired to
 * react-hook-form. The single home for the watch/setValue/error wiring so
 * every form mounts the country picker identically. The field's value
 * convention is the app-wide form one: '' means "not provided"; codes
 * otherwise (legacy free text may appear at load and is handled by
 * CountrySelect + the schema's `isResolvableCountryInput` refine).
 */
export function CountrySelectField<TValues extends FieldValues>({
  form,
  name,
  id,
  label = 'Country',
  disabled,
}: CountrySelectFieldProps<TValues>) {
  const error = form.getFieldState(name, form.formState).error?.message;
  const value = form.watch(name) as string | undefined;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <CountrySelect
        id={id}
        value={value || null}
        onChange={(code) =>
          form.setValue(name, (code ?? '') as PathValue<TValues, Path<TValues>>, {
            shouldValidate: true,
            shouldDirty: true,
          })
        }
        hasError={Boolean(error)}
        disabled={disabled}
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
